import { Component, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { DeviceTypeParameterConfigWithAvailableListAndParameter } from '@vectronic/util';

type Config = DeviceTypeParameterConfigWithAvailableListAndParameter;

@Component({
  selector: 'vectronic-parameter-timespan-config',
  templateUrl: './parameter-timespan-config.component.html',
  styleUrls: ['./parameter-timespan-config.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    MatInput,
    FormsModule,
    MatIcon,
    MatTooltip,
    MatIconButton,
  ],
})
export class ParameterTimespanConfigComponent {
  config = input<Config>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  resetToFactoryTooltip = input<string>();

  hasClose = input(false);
  hideLabel = input<boolean>();
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  configClose = output();
  parameterChange = output<{ config: Config; value: string }>();

  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
  millisecond: string;
  calculatedValue: number;
  hasError = false;

  valueSubscription = toObservable(this.value)
    .pipe(takeUntilDestroyed())
    .subscribe((val) => {
      if (!isNaN(parseInt(val))) {
        this.updateFields(val);
      } else {
        this.updateFields(undefined);
      }
    });

  updateFields(value: string) {
    const convertedMilliseconds = this.convertMilliseconds(
      parseInt(value),
      this.hasYear(),
      this.hasMonth(),
      this.hasDay(),
      this.hasHour(),
      this.hasMinute(),
      this.hasSecond(),
    );
    this.year = convertedMilliseconds.years.toString();
    this.month = convertedMilliseconds.months.toString();
    this.day = convertedMilliseconds.days.toString();
    this.hour = convertedMilliseconds.hours.toString();
    this.minute = convertedMilliseconds.minutes.toString();
    this.second = convertedMilliseconds.seconds.toString();
    this.millisecond = convertedMilliseconds.milliseconds.toString();
  }

  onChangeYear($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    this.year = parseInt(value) < 0 ? '0' : value;
    this.calculate();
  }

  onChangeMonth($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.month = '0';
    } else if (parseInt(value) >= 11) {
      this.month = '11';
    } else {
      this.month = value;
    }
    this.calculate();
  }

  onChangeDay($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.day = '0';
    } else if (parseInt(value) >= 29) {
      this.day = '29';
    } else {
      this.day = value;
    }
    this.calculate();
  }

  onChangeHour($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.hour = '0';
    } else if (parseInt(value) >= 23) {
      this.hour = '23';
    } else {
      this.hour = value;
    }
    this.calculate();
  }

  onChangeMinute($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.minute = '0';
    } else if (parseInt(value) >= 59) {
      this.minute = '59';
    } else {
      this.minute = value;
    }
    this.calculate();
  }

  onChangeSecond($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.second = '0';
    } else if (parseInt(value) >= 59) {
      this.second = '59';
    } else {
      this.second = value;
    }
    this.calculate();
  }

  onChangeMillisecond($event: Event) {
    const { value } = $event.target as HTMLInputElement;
    if (parseInt(value) < 0) {
      this.millisecond = '0';
    } else if (parseInt(value) >= 999) {
      this.millisecond = '999';
    } else {
      this.millisecond = value;
    }
    this.calculate();
  }

  convertMilliseconds(
    milliseconds: number,
    hasYear = true,
    hasMonth = true,
    hasDay = true,
    hasHour = true,
    hasMinute = true,
    hasSecond = true,
  ) {
    const msPerSecond = 1000;
    const msPerMinute = 60 * msPerSecond;
    const msPerHour = 60 * msPerMinute;
    const msPerDay = 24 * msPerHour;
    const msPerMonth = 30 * msPerDay;
    const msPerYear = 365 * msPerDay;

    let years = Math.floor(milliseconds / msPerYear);
    milliseconds %= msPerYear;

    let months = Math.floor(milliseconds / msPerMonth);
    milliseconds %= msPerMonth;

    let days = Math.floor(milliseconds / msPerDay);
    milliseconds %= msPerDay;

    let hours = Math.floor(milliseconds / msPerHour);
    milliseconds %= msPerHour;

    let minutes = Math.floor(milliseconds / msPerMinute);
    milliseconds %= msPerMinute;

    let seconds = Math.floor(milliseconds / msPerSecond);
    let remainingMilliseconds = milliseconds % msPerSecond;

    if (!hasYear) {
      months += years * 12;
      years = 0;
    }
    if (!hasMonth) {
      days += months * 30;
      months = 0;
    }
    if (!hasDay) {
      hours += days * 24;
      days = 0;
    }
    if (!hasHour) {
      minutes += hours * 60;
      hours = 0;
    }
    if (!hasMinute) {
      seconds += minutes * 60;
      minutes = 0;
    }
    if (!hasSecond) {
      remainingMilliseconds += seconds * 1000;
      seconds = 0;
    }

    return {
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      milliseconds: remainingMilliseconds,
    };
  }

  calculate() {
    this.calculatedValue =
      parseInt(this.getValueAsParsableInt(this.year)) * 60 * 60 * 1000 * 24 * 365 +
      parseInt(this.getValueAsParsableInt(this.month)) * 60 * 60 * 1000 * 24 * 30 +
      parseInt(this.getValueAsParsableInt(this.day)) * 60 * 60 * 1000 * 24 +
      parseInt(this.getValueAsParsableInt(this.hour)) * 60 * 60 * 1000 +
      parseInt(this.getValueAsParsableInt(this.minute)) * 60 * 1000 +
      parseInt(this.getValueAsParsableInt(this.second)) * 1000 +
      parseInt(this.getValueAsParsableInt(this.millisecond));
    if (this.config().parsedMax && this.calculatedValue > parseInt(this.config().parsedMax)) {
      this.calculatedValue = parseInt(this.config().parsedMax);
      this.updateFields(this.calculatedValue.toString());
    }
    if (this.config().parsedMin && this.calculatedValue < parseInt(this.config().parsedMin)) {
      this.calculatedValue = parseInt(this.config().parsedMin);
      this.updateFields(this.calculatedValue.toString());
    }
    this.parameterChange.emit({
      config: this.config(),
      value: this.calculatedValue ? this.calculatedValue.toString() : undefined,
    });
  }

  getValueAsParsableInt(value: string) {
    return value !== 'NaN' ? value || '0' : '0';
  }

  getTopUnits() {
    const parsedMax = parseInt(this.config().parsedMax);
    if (!parsedMax) {
      return ['millisecond'];
    }

    const units = [
      { name: 'year', threshold: 1000 * 60 * 60 * 24 * 365 },
      { name: 'month', threshold: 1000 * 60 * 60 * 24 * 30 },
      { name: 'day', threshold: 1000 * 60 * 60 * 24 },
      { name: 'hour', threshold: 1000 * 60 * 60 },
      { name: 'minute', threshold: 1000 * 60 },
      { name: 'second', threshold: 1000 },
      { name: 'millisecond', threshold: 1 },
    ];

    const applicableUnits = units.filter((unit) => parsedMax >= unit.threshold);

    return applicableUnits.slice(0, 3).map((unit) => unit.name);
  }

  hasYear() {
    return this.getTopUnits().includes('year');
  }

  hasMonth() {
    return this.getTopUnits().includes('month');
  }

  hasDay() {
    return this.getTopUnits().includes('day');
  }

  hasHour() {
    return this.getTopUnits().includes('hour');
  }

  hasMinute() {
    return this.getTopUnits().includes('minute');
  }

  hasSecond() {
    return this.getTopUnits().includes('second');
  }

  hasMillisecond() {
    return this.getTopUnits().includes('millisecond');
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.day = undefined;
    this.hour = undefined;
    this.minute = undefined;
    this.second = undefined;
    this.millisecond = undefined;
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }
}
